import { Upload } from 'lucide-react';
import { type ChangeEvent, useRef, useState } from 'react';
import type { BulkImportResult, BulkImportUser } from '../../hooks/useAdminData';
import { useBulkImportUsers } from '../../hooks/useAdminData';
import { Badge } from '../Badge';
import { Modal } from '../Modal';

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
}

type Step = 'upload' | 'preview' | 'importing' | 'results';

interface ParsedRow extends BulkImportUser {
  _valid: boolean;
  _error?: string;
}

function parseCSV(text: string): ParsedRow[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const emailIdx = headers.indexOf('email');
  const nameIdx = headers.indexOf('fullname');
  const roleIdx = headers.indexOf('role');
  const orgIdx = headers.indexOf('organizationid');
  const orgRoleIdx = headers.indexOf('orgrole');

  if (emailIdx === -1 || nameIdx === -1) return [];

  return lines.slice(1).map((line) => {
    const cols = line.split(',').map((c) => c.trim());
    const email = cols[emailIdx] ?? '';
    const fullName = cols[nameIdx] ?? '';
    const role = roleIdx >= 0 ? cols[roleIdx] : undefined;
    const organizationId = orgIdx >= 0 ? cols[orgIdx] : undefined;
    const orgRole = orgRoleIdx >= 0 ? cols[orgRoleIdx] : undefined;

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const valid = emailValid && fullName.length > 0;

    return {
      email,
      fullName,
      role: role || undefined,
      organizationId: organizationId || undefined,
      orgRole: orgRole || undefined,
      _valid: valid,
      _error: !email
        ? 'Missing email'
        : !emailValid
          ? 'Invalid email format'
          : !fullName
            ? 'Missing fullName'
            : undefined,
    };
  });
}

function parseJSON(text: string): ParsedRow[] {
  const items = JSON.parse(text) as BulkImportUser[];
  return items.map((item) => {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.email ?? '');
    const valid = emailValid && (item.fullName ?? '').length > 0;
    return {
      ...item,
      _valid: valid,
      _error: !item.email
        ? 'Missing email'
        : !emailValid
          ? 'Invalid email format'
          : !item.fullName
            ? 'Missing fullName'
            : undefined,
    };
  });
}

const CSV_TEMPLATE =
  'email,fullName,role,organizationId,orgRole\nuser@example.com,John Doe,sales_rep,,';

export function BulkImportModal({ open, onClose }: BulkImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [parseError, setParseError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const bulkImport = useBulkImportUsers();

  function reset() {
    setStep('upload');
    setRows([]);
    setResult(null);
    setParseError('');
  }

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      try {
        const parsed = file.name.endsWith('.json') ? parseJSON(text) : parseCSV(text);
        if (parsed.length === 0) {
          setParseError('No valid rows found. Ensure headers include email and fullName.');
          return;
        }
        setRows(parsed);
        setParseError('');
        setStep('preview');
      } catch {
        setParseError('Failed to parse file. Ensure valid CSV or JSON format.');
      }
    };
    reader.readAsText(file);
  }

  function handleImport() {
    const validRows = rows.filter((r) => r._valid);
    if (validRows.length === 0) return;

    setStep('importing');
    bulkImport.mutate(
      {
        users: validRows.map(({ _valid, _error, ...rest }) => rest),
      },
      {
        onSuccess: (res) => {
          setResult(res.data);
          setStep('results');
        },
        onError: () => {
          setStep('preview');
        },
      },
    );
  }

  function downloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const validCount = rows.filter((r) => r._valid).length;
  const invalidCount = rows.length - validCount;

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Bulk Import Users"
      size="xl"
    >
      {step === 'upload' && (
        <div className="space-y-4">
          <div
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl py-12 cursor-pointer hover:border-primary/50 transition-colors"
          >
            <Upload size={32} className="text-text-muted mb-3" />
            <p className="text-sm text-text-secondary">Click to upload CSV or JSON file</p>
            <p className="text-xs text-text-muted mt-1">Max 500 users per import</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.json"
              onChange={handleFile}
              className="hidden"
            />
          </div>
          {parseError && <p className="text-sm text-danger">{parseError}</p>}
          <button
            onClick={downloadTemplate}
            className="text-sm text-primary hover:text-primary-hover transition-colors"
          >
            Download CSV template
          </button>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant="success">{validCount} valid</Badge>
            {invalidCount > 0 && <Badge variant="danger">{invalidCount} invalid</Badge>}
            <span className="text-sm text-text-muted">{rows.length} total rows</span>
          </div>
          <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted bg-background">
                  <th className="px-3 py-2 font-medium">#</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-border">
                    <td className="px-3 py-2 text-text-muted">{i + 1}</td>
                    <td className="px-3 py-2 text-text">{row.email}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.fullName}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.role ?? '-'}</td>
                    <td className="px-3 py-2">
                      {row._valid ? (
                        <Badge variant="success">Valid</Badge>
                      ) : (
                        <Badge variant="danger">{row._error}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={reset}
              className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleImport}
              disabled={validCount === 0}
              className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import {validCount} Users
            </button>
          </div>
        </div>
      )}

      {step === 'importing' && (
        <div className="flex flex-col items-center py-8">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm text-text-secondary">Importing users...</p>
        </div>
      )}

      {step === 'results' && result && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant="success">{result.created} created</Badge>
            {result.failed > 0 && <Badge variant="danger">{result.failed} failed</Badge>}
          </div>
          {result.errors.length > 0 && (
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-text-muted bg-background">
                    <th className="px-3 py-2 font-medium">Row</th>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((err, i) => (
                    <tr key={i} className="border-b border-border">
                      <td className="px-3 py-2 text-text-muted">{err.row}</td>
                      <td className="px-3 py-2 text-text">{err.email}</td>
                      <td className="px-3 py-2 text-danger">{err.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <button
              onClick={() => {
                reset();
                onClose();
              }}
              className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
